import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { apiErrorMessage } from '../../pages/admin/transactions/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const zar = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Which workbook column each portal-created loan's first draw is stored in
 * (CashflowValue = interest from the first day; CashflowValue1 = no first-day
 * interest, e.g. a same-day recycle). The partial upload stored every draw as
 * CashflowValue; set the real column here. Workbook loans aren't listed.
 * Backend: routers/loan_cashflow_upload.py (first-draws).
 */
export function FirstDrawReviewCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/loan-cashflows/first-draws`, { headers });
      setData(r.data);
      setSel(new Set());
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not load portal-created loans'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.loans || []).filter(l => !s || [l.loan_id, l.client_id, l.date, ...(l.rp || []).map(r => r.risk_participant)]
      .some(x => String(x || '').toLowerCase().includes(s)));
  }, [data, q]);

  const apply = async (column) => {
    if (!sel.size) return;
    const reason = window.prompt(
      `Store the first draw of ${sel.size} loan(s) in ${column}.\n\nReason for the audit trail (optional), e.g. "Same day recycle captured as new loan":`, '');
    if (reason === null) return;  // cancelled
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/loan-cashflows/first-draws/set`,
        { loan_ids: [...sel], cashflow_column: column, reason }, { headers });
      toast.success(r.data.message);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Update failed'));
    } finally { setBusy(false); }
  };

  const toggle = (id) => setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allShown = rows.length > 0 && rows.every(r => sel.has(r.loan_id));

  return (
    <Card data-testid="first-draw-review-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between gap-2">
          First draw column (portal-created loans)
          <Button variant="ghost" size="sm" onClick={load} title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
        </CardTitle>
        <CardDescription>
          Loans created by the partial upload or + New Deal, and the workbook column their first draw is stored in.
          The partial upload stored every draw in <strong>CashflowValue</strong>, which earns interest from its first day. A same-day
          recycle's draw belongs in <strong>CashflowValue1</strong>, which doesn't. Tick the loans and set the column they're in on
          your workbook. Workbook loans aren't listed; their columns come from the workbook.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!data ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span>{data.total} loan(s): {Object.entries(data.by_column || {}).map(([c, n]) => `${n} in ${c}`).join(', ')}</span>
              <Input placeholder="Filter by loan, client, RP or date" value={q} onChange={e => setQ(e.target.value)} className="w-64 h-8" />
              <Button size="sm" variant="outline" disabled={busy || !sel.size} onClick={() => apply('CashflowValue1')} data-testid="first-draw-set-cv1">
                Set {sel.size || ''} to CashflowValue1
              </Button>
              <Button size="sm" variant="outline" disabled={busy || !sel.size} onClick={() => apply('CashflowValue')} data-testid="first-draw-set-cv">
                Set {sel.size || ''} to CashflowValue
              </Button>
            </div>
            <div className="max-h-96 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 text-left">
                  <tr>
                    <th className="p-2 w-8"><input type="checkbox" checked={allShown}
                      onChange={e => setSel(e.target.checked ? new Set(rows.map(r => r.loan_id)) : new Set())} /></th>
                    <th className="p-2">Loan</th><th className="p-2">Client</th><th className="p-2">Draw date</th>
                    <th className="p-2 text-right">LB draw</th><th className="p-2">RP tranches</th>
                    <th className="p-2">Stored in</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(l => (
                    <tr key={l.loan_id} className={`border-t ${sel.has(l.loan_id) ? 'bg-blue-50' : ''}`}>
                      <td className="p-2"><input type="checkbox" checked={sel.has(l.loan_id)} onChange={() => toggle(l.loan_id)} /></td>
                      <td className="p-2 font-mono">{l.loan_id}</td>
                      <td className="p-2">{l.client_id}</td>
                      <td className="p-2">{l.date}</td>
                      <td className="p-2 text-right font-mono">{zar(l.lb_amount)}</td>
                      <td className="p-2">{(l.rp || []).map(r => `${r.risk_participant} ${zar(r.amount)}`).join(', ')}</td>
                      <td className="p-2">
                        <Badge className={l.column === 'CashflowValue' ? 'bg-slate-100 text-slate-700' : l.column.startsWith('mixed') ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}>{l.column}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default FirstDrawReviewCard;
